/**
 * BackupServiceRegistry
 *
 * Central registry for backup/restore service configurations.
 * Packages self-register their backup/restore capabilities without
 * requiring changes to the central backup controller.
 *
 * This enables a schema-agnostic, plugin-style architecture where:
 * - Each package defines its own backup/restore logic
 * - No hard-coded collection names in controllers
 * - Adding new packages requires NO changes to backup infrastructure
 */

class BackupServiceRegistry {
  constructor() {
    this.services = new Map();
    console.log('[BackupServiceRegistry] Initialized');
  }

  /**
   * Register a service for backup/restore operations
   *
   * @param {string} serviceName - Unique identifier for the service
   * @param {Object} serviceConfig - Service configuration
   * @param {Function} serviceConfig.fetchAll - Async function to fetch all documents
   * @param {Function} serviceConfig.restoreAll - Async function to restore documents
   * @param {Function} [serviceConfig.fetchStorage] - Optional: fetch storage files
   * @param {Function} [serviceConfig.restoreStorage] - Optional: restore storage files
   * @param {string} [serviceConfig.version] - Service version (default: '1.0')
   */
  register(serviceName, serviceConfig) {
    if (!serviceName || typeof serviceName !== 'string') {
      throw new Error('[BackupServiceRegistry] Service name must be a non-empty string');
    }

    if (!serviceConfig || typeof serviceConfig !== 'object') {
      throw new Error('[BackupServiceRegistry] Service config must be an object');
    }

    if (typeof serviceConfig.fetchAll !== 'function') {
      throw new Error(`[BackupServiceRegistry] ${serviceName}: fetchAll must be a function`);
    }

    if (typeof serviceConfig.restoreAll !== 'function') {
      throw new Error(`[BackupServiceRegistry] ${serviceName}: restoreAll must be a function`);
    }

    // Validate optional storage functions if provided
    if (serviceConfig.fetchStorage && typeof serviceConfig.fetchStorage !== 'function') {
      throw new Error(`[BackupServiceRegistry] ${serviceName}: fetchStorage must be a function`);
    }

    if (serviceConfig.restoreStorage && typeof serviceConfig.restoreStorage !== 'function') {
      throw new Error(`[BackupServiceRegistry] ${serviceName}: restoreStorage must be a function`);
    }

    this.services.set(serviceName, {
      name: serviceName,
      fetchAll: serviceConfig.fetchAll,
      restoreAll: serviceConfig.restoreAll,
      fetchStorage: serviceConfig.fetchStorage,
      restoreStorage: serviceConfig.restoreStorage,
      version: serviceConfig.version || '1.0'
    });

    console.log(`[BackupServiceRegistry] Registered service: ${serviceName} (v${serviceConfig.version || '1.0'})`);
  }

  /**
   * Get all registered services
   * @returns {Array} Array of service configurations
   */
  getAllServices() {
    return Array.from(this.services.values());
  }

  /**
   * Get a specific service by name
   * @param {string} name - Service name
   * @returns {Object|undefined} Service configuration or undefined if not found
   */
  getService(name) {
    return this.services.get(name);
  }

  /**
   * Check if a service is registered
   * @param {string} name - Service name
   * @returns {boolean} True if service is registered
   */
  hasService(name) {
    return this.services.has(name);
  }

  /**
   * Unregister a service (primarily for testing)
   * @param {string} name - Service name
   * @returns {boolean} True if service was removed
   */
  unregister(name) {
    const removed = this.services.delete(name);
    if (removed) {
      console.log(`[BackupServiceRegistry] Unregistered service: ${name}`);
    }
    return removed;
  }

  /**
   * Get count of registered services
   * @returns {number} Number of registered services
   */
  getServiceCount() {
    return this.services.size;
  }

  /**
   * Clear all registered services (primarily for testing)
   */
  clear() {
    this.services.clear();
    console.log('[BackupServiceRegistry] Cleared all registered services');
  }
}

// Export singleton instance
export const backupRegistry = new BackupServiceRegistry();
